// src/components/chat/ChatMessage.js
import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { formatDateTime } from '../../utils/formatting';
import AnalysisDisplay from '../analysis/AnalysisDisplay';
// NEU: LegalReferencesDisplay importieren
import LegalReferencesDisplay from './LegalReferencesDisplay';

// NEU: Hilfsfunktion zum Prüfen auf Rechtsreferenz-JSON
const isLegalReferencesJson = (content) => {
  if (typeof content !== 'string' || !content.trim().startsWith('{')) {
    return null; // Kein String oder kein JSON-Objekt
  }
  try {
    const parsed = JSON.parse(content);
    // Prüfen, ob die *spezifischen* Schlüssel für Referenzen vorhanden sind
    if (
      parsed &&
      typeof parsed === 'object' && // Sicherstellen, dass es ein Objekt ist
      Array.isArray(parsed.relevante_gesetzesstellen) &&
      Array.isArray(parsed.urteil_suche) &&
      Array.isArray(parsed.law_list) &&
      typeof parsed.topic === 'string'
    ) {
      return parsed; // Gültiges Referenz-JSON
    }
    return null; // Gültiges JSON, aber nicht das erwartete Referenz-Format
  } catch (e) {
    return null; // Ungültiges JSON
  }
};

const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // --- Analyse-Prüfung (bestehende Logik) ---
  const isAnalysis =
    isAssistant &&
    (message.analysis ||
      (message.content &&
        typeof message.content === 'string' &&
        message.content.includes('{"Analyse_Rechtsprechungsmuster"'))); // Oder eine andere eindeutige Kennung für Analyse

  const getAnalysisData = () => {
    if (message.analysis && typeof message.analysis === 'object') {
      return message.analysis;
    } else if (isAnalysis && message.content && typeof message.content === 'string') {
      try {
        return JSON.parse(message.content);
      } catch (e) {
        console.error('Fehler beim Parsen der Analyse:', e);
        return null;
      }
    }
    return null;
  };
  const analysisData = isAnalysis ? getAnalysisData() : null;
  // --- Ende Analyse-Prüfung ---

  // --- NEU: Rechtsreferenz-Prüfung ---
  // Nur prüfen, wenn es *keine* Analyse ist, um Überschneidungen zu vermeiden
  const legalReferencesData =
    isAssistant && !isAnalysis ? isLegalReferencesJson(message.content) : null;
  // --- Ende Rechtsreferenz-Prüfung ---

  // Format creation date
  const formattedDate = formatDateTime(message.createdAt);

  // Bestimmen, ob es sich um speziellen Inhalt handelt (für Styling)
  const isSpecialContent = isAnalysis || !!legalReferencesData;

  return (
    <MessageContainer $isUser={isUser}>
      <MessageHeader>
        <MessageRole $isUser={isUser}>
          {/* Titel anpassen je nach Inhalt */}
          {isUser
            ? 'Sie'
            : isAnalysis
              ? 'KI-Analyse'
              : legalReferencesData
                ? 'KI-Recherche' // Oder "Rechtsreferenzen"
                : 'Law-AI'}
        </MessageRole>
        <MessageTime>{formattedDate}</MessageTime>
      </MessageHeader>

      {/* Styled Component Prop anpassen */}
      <MessageContent $isUser={isUser} $isSpecialContent={isSpecialContent}>
        {/* Logik erweitern: Analyse -> Referenzen -> Text */}
        {isAnalysis && analysisData ? (
          <AnalysisDisplay analysis={analysisData} />
        ) : legalReferencesData ? (
          <LegalReferencesDisplay data={legalReferencesData} />
        ) : (
          <MessageText>{message.content}</MessageText>
        )}
      </MessageContent>
    </MessageContainer>
  );
};

// --- Styled Components ---
// WICHTIG: Styled Components anpassen, um $isSpecialContent zu verwenden
//          und das Styling für Analyse/Referenzen korrekt zu handhaben.

const MessageContainer = styled.div`
  margin-bottom: 1.5rem;
  max-width: 90%;
  /* Props mit $ prefix versehen */
  align-self: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  /* Sicherstellen, dass der Header nicht vom Inhalt überdeckt wird, falls dieser keinen eigenen Hintergrund hat */
  padding: 0 0.2rem; /* Kleiner horizontaler Padding, falls Inhalt transparent ist */
`;

const MessageRole = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  /* Props mit $ prefix versehen */
  color: ${({ $isUser }) =>
    $isUser ? theme.colors.primary : theme.colors.secondary};
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
  margin-left: 0.5rem;
`;

const MessageContent = styled.div`
  /* Props mit $ prefix versehen */
  /* Hintergrund: Transparent für spezielle Inhalte, sonst basierend auf User/AI */
  background-color: ${({ $isUser, $isSpecialContent }) =>
    $isSpecialContent
      ? 'transparent'
      : $isUser
        ? `${theme.colors.primary}10`
        : theme.colors.backgroundAlt};

  /* Padding: Kein Padding für spezielle Inhalte (Komponenten bringen eigenes mit), sonst Standard */
  padding: ${({ $isSpecialContent }) => ($isSpecialContent ? '0' : '1rem')};

  border-radius: ${theme.borderRadius.md};

  /* Border: Keine Border für spezielle Inhalte (Komponenten bringen eigene mit), sonst Standard */
  border: ${({ $isSpecialContent }) =>
    $isSpecialContent ? 'none' : `1px solid ${theme.colors.borderLight}`};

  overflow-wrap: break-word;

  /* WICHTIG: Sicherstellen, dass die inneren Komponenten (AnalysisDisplay, LegalReferencesDisplay)
     ihren eigenen Container (Hintergrund, Padding, Border) korrekt darstellen.
     Diese äußere MessageContent dient nur als Wrapper, wenn $isSpecialContent true ist. */
`;

const MessageText = styled.p`
  margin: 0;
  white-space: pre-wrap; /* Wichtig für Zeilenumbrüche im Text */
  line-height: 1.5;
`;

export default ChatMessage;
